import { SelectQuery, SimpleSelectQuery } from "../models/SelectQuery";
import { SelectQueryParser } from "../parsers/SelectQueryParser";
import { SqlSortInjector, SortConditions } from "./SqlSortInjector";
import { SqlPaginationInjector, PaginationOptions } from "./SqlPaginationInjector";
import { QueryBuilder } from "./QueryBuilder";
import { SqlParameterBinder } from "./SqlParameterBinder";
import { ParameterDetector } from "../utils/ParameterDetector";
import { ColumnReference, SqlParameterValue } from "../models/ValueComponent";
import { SelectItem } from "../models/Clause";
import {
    SchemaInfo,
    optimizeUnusedLeftJoinsToFixedPoint,
    optimizeUnusedCtesToFixedPoint
} from "./OptimizeUnusedLeftJoins";
import {
    OptionalConditionParameterStates,
    OptionalConditionPruningParameters,
    pruneOptionalConditionBranches
} from "./PruneOptionalConditionBranches";
import type { ExistsSubqueryDefinition } from "./ExistsPredicateInjector";

export type { ExistsSubqueryDefinition };

/**
 * Object-form filter condition supporting scalar operators, logical grouping,
 * and column-anchored EXISTS/NOT EXISTS predicates.
 *
 * @example
 * const filter: FilterConditionObject = {
 *   min: 10,
 *   max: 100,
 *   exists: { sql: 'SELECT 1 FROM orders WHERE user_id = $c0' }
 * };
 * Related tests: packages/core/tests/transformers/DynamicQueryBuilder.test.ts
 */
export interface FilterConditionObject {
    min?: SqlParameterValue;
    max?: SqlParameterValue;
    like?: string;
    ilike?: string;
    in?: SqlParameterValue[];
    any?: SqlParameterValue[];
    '='?: SqlParameterValue;
    '>'?: SqlParameterValue;
    '<'?: SqlParameterValue;
    '>='?: SqlParameterValue;
    '<='?: SqlParameterValue;
    '!='?: SqlParameterValue;
    '<>'?: SqlParameterValue;
    or?: { column: string; [operator: string]: SqlParameterValue | string }[];
    and?: { column: string; [operator: string]: SqlParameterValue | string }[];

    column?: string;
    exists?: ExistsSubqueryDefinition;
    notExists?: ExistsSubqueryDefinition;
}

export interface MultiColumnExistsDefinition extends ExistsSubqueryDefinition {
    on: string[];
}

export type FilterConditionValue =
    | SqlParameterValue
    | SqlParameterValue[]
    | FilterConditionObject
    | MultiColumnExistsDefinition[];


/**
 * Filter conditions for dynamic query building.
 *
 * Supports both unqualified and qualified column names:
 * - Unqualified: `{ name: 'Alice' }` - applies to all columns named 'name'
 * - Qualified: `{ 'users.name': 'Bob' }` - applies only to the 'name' column in the 'users' table/alias
 * - Hybrid: `{ name: 'Default', 'users.name': 'Override' }` - qualified names take priority over unqualified
 *
 * @example
 * ```typescript
 * // Basic usage (backward compatible)
 * const filter: FilterConditions = {
 *   name: 'Alice',
 *   status: 'active'
 * };
 *
 * // Qualified names for disambiguation in JOINs
 * const filter: FilterConditions = {
 *   'users.name': 'Alice',    // Only applies to users.name
 *   'profiles.name': 'Bob'    // Only applies to profiles.name
 * };
 *
 * // Hybrid approach
 * const filter: FilterConditions = {
 *   status: 'active',         // Applies to all 'status' columns
 *   'users.name': 'Alice',    // Overrides for users.name specifically
 *   'profiles.name': 'Bob'    // Overrides for profiles.name specifically
 * };
 * ```
 * Related tests: packages/core/tests/transformers/DynamicQueryBuilder.test.ts
 */
export type FilterConditions = Record<string, FilterConditionValue>;



/**
 * Options for dynamic query building
 */
export interface QueryBuildOptions {
    /**
     * Legacy filter input for named-parameter binding only.
     * Dynamic predicate injection is no longer supported and will fail fast.
     */
    filter?: FilterConditions;
    /** Sort conditions to inject into ORDER BY clause */
    sort?: SortConditions;
    /** Pagination options to inject LIMIT/OFFSET clauses */
    paging?: PaginationOptions;
    /**
     * Columns that should remain in the SELECT clause.
     * When specified, every other column is removed so the output matches this whitelist.
     */
    includeColumns?: string[];
    /**
     * Columns that should be removed from the SELECT clause.
     * Filters apply subtractively and only drop columns that exist in the original output.
     */
    excludeColumns?: string[];
    /**
     * Throw when column-anchored EXISTS filters fail to resolve.
     * Defaults to false so invalid definitions are skipped silently.
     */
    existsStrict?: boolean;
    /**
     * Schema metadata used when removing unused LEFT JOINs; overrides builder defaults.
     */
    schemaInfo?: SchemaInfo;
    /**
     * Remove unused LEFT JOINs before further processing when schema info is available.
     */
    removeUnusedLeftJoins?: boolean;
    /**
     * Remove unused Common Table Expressions (CTEs) when they can be safely pruned.
     * Defaults to false to preserve original WITH definitions.
     */
    removeUnusedCtes?: boolean;
    /**
     * Explicit opt-in values for truthful optional condition branches in source SQL.
     * Only listed parameter names are eligible for pruning, and `null`/`undefined` are treated as absent-equivalent.
     */
    optionalConditionParameters?: OptionalConditionPruningParameters;
    /**
     * Legacy state-map form for optional branch pruning.
     * Prefer `optionalConditionParameters` for new code so SQL-facing null semantics stay intuitive.
     */
    optionalConditionParameterStates?: OptionalConditionParameterStates;
}

/**
 * Builder-level configuration that can be reused across multiple build calls.
 */
export interface DynamicQueryBuilderOptions {
    /** Optional resolver for table column names (retains backward compatibility). */
    tableColumnResolver?: (tableName: string) => string[];
    /**
     * Schema metadata that may be applied by default when the optimizer is enabled.
     * Schema info provided via QueryBuildOptions takes precedence.
     */
    schemaInfo?: SchemaInfo;
}

/**
 * DynamicQueryBuilder combines SQL parsing with dynamic condition injection (filters, sorts, paging).
 *
 * Key behaviours verified in packages/core/tests/transformers/DynamicQueryBuilder.test.ts:
 * - Preserves the input SQL when no options are supplied.
 * - Applies filter, sort, and pagination in a deterministic order.
 * - Fails fast for removed SQL-result JSON shaping.
 */
export class DynamicQueryBuilder {
    private tableColumnResolver?: (tableName: string) => string[];
    private defaultSchemaInfo?: SchemaInfo;

    /**
     * Creates a new DynamicQueryBuilder instance.
     * Accepts either the legacy table resolver or an options object that can provide schema metadata.
     *
     * @param resolverOrOptions Optional resolver or configuration object
     */
    constructor(
        resolverOrOptions?: ((tableName: string) => string[]) | DynamicQueryBuilderOptions
    ) {
        if (typeof resolverOrOptions === "function") {
            this.tableColumnResolver = resolverOrOptions;
        } else if (resolverOrOptions) {
            this.tableColumnResolver = resolverOrOptions.tableColumnResolver;
            this.defaultSchemaInfo = resolverOrOptions.schemaInfo;
        }
    }

    /**
     * Builds a SelectQuery from SQL content with dynamic conditions.
     * This is a pure function that does not perform any I/O operations.
     * @param sqlContent Raw SQL string to parse and modify
     * @param options Dynamic conditions to apply (filter, sort, paging)
     * @returns Modified SelectQuery with all dynamic conditions applied
     * @example
     * ```typescript
     * const builder = new DynamicQueryBuilder();
     * const query = builder.buildQuery(
     *   'SELECT id, name FROM users WHERE active = true',
     *   {
     *     filter: { status: 'premium' },
     *     sort: { created_at: { desc: true } },
     *     paging: { page: 2, pageSize: 10 }
     *   }
     * );
     * ```
     */
    buildQuery(sqlContent: string, options: QueryBuildOptions = {}): SelectQuery {
        const removedOptions = options as Record<string, unknown>;
        if ('serialize' in removedOptions || 'jsonb' in removedOptions) {
            throw new Error(
                "DynamicQueryBuilder SQL-result JSON shaping has been removed. Keep SQL results as rows and use generated AOT mappers so the executed SQL remains debuggable."
            );
        }

        // Parse the base SQL
        let parsedQuery: SimpleSelectQuery;
        try {
            parsedQuery = SelectQueryParser.parse(sqlContent) as SimpleSelectQuery;
        } catch (error) {
            throw new Error(`Failed to parse SQL: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }

        // Apply dynamic modifications in the correct order
        let modifiedQuery: SelectQuery = parsedQuery;

        // 1. Bind existing named parameters, but fail fast for legacy runtime filter assembly.
        if (options.filter && Object.keys(options.filter).length > 0) {
            const { hardcodedParams, dynamicFilters } = ParameterDetector.separateFilters(modifiedQuery, options.filter);

            // Bind hardcoded parameters if any exist
            if (Object.keys(hardcodedParams).length > 0) {
                const parameterBinder = new SqlParameterBinder({ requireAllParameters: false });
                modifiedQuery = parameterBinder.bind(modifiedQuery, hardcodedParams);
            }

            const hasLegacyDynamicFilters = Object.keys(dynamicFilters).length > 0;
            if (hasLegacyDynamicFilters) {
                throw new Error(
                    "DynamicQueryBuilder no longer injects runtime filter predicates. Use `ashiba query optional add` to author optional filters, `ashiba query optional refresh` to refresh them, and `optionalConditionParameters` at runtime for pruning only."
                );
            }
        }

        // 2. Apply sorting second (after filtering to sort smaller dataset)
        if (options.sort && Object.keys(options.sort).length > 0) {
            const sortInjector = new SqlSortInjector(this.tableColumnResolver);
            // Ensure we have a SimpleSelectQuery for the injector
            const simpleQuery = QueryBuilder.buildSimpleQuery(modifiedQuery);
            modifiedQuery = sortInjector.inject(simpleQuery, options.sort);
        }

        // 3. Apply pagination third (after filtering and sorting)
        if (options.paging) {
            const { page = 1, pageSize } = options.paging;
            if (pageSize !== undefined) {
                const paginationInjector = new SqlPaginationInjector();
                const paginationOptions = { page, pageSize };
                // Ensure we have a SimpleSelectQuery for the injector
                const simpleQuery = QueryBuilder.buildSimpleQuery(modifiedQuery);
                modifiedQuery = paginationInjector.inject(simpleQuery, paginationOptions);
            }
        }
        // 4. Apply column projection filters before any optimizer passes.
        modifiedQuery = this.applyColumnFilters(modifiedQuery, options);
        // 5. Prune supported truthful optional branches before structural optimizers.
        const optionalConditionParameters = this.resolveOptionalConditionPruningParameters(options);
        if (Object.keys(optionalConditionParameters).length > 0) {
            modifiedQuery = pruneOptionalConditionBranches(modifiedQuery, optionalConditionParameters);
        }
        // 6. Remove unused LEFT JOINs when asked.
        const effectiveSchemaInfo = options.schemaInfo ?? this.defaultSchemaInfo;
        if (options.removeUnusedLeftJoins && effectiveSchemaInfo?.length) {
            modifiedQuery = optimizeUnusedLeftJoinsToFixedPoint(modifiedQuery, effectiveSchemaInfo);
        }
        // 7. Remove unused CTEs when requested.
        if (options.removeUnusedCtes) {
            modifiedQuery = optimizeUnusedCtesToFixedPoint(modifiedQuery);
        }
        return modifiedQuery;
    }

    private resolveOptionalConditionPruningParameters(options: QueryBuildOptions): OptionalConditionPruningParameters {
        if (options.optionalConditionParameters) {
            return options.optionalConditionParameters;
        }

        if (!options.optionalConditionParameterStates) {
            return {};
        }

        const legacyParameters: OptionalConditionPruningParameters = {};

        // Preserve backward compatibility for the state-map API while the value-based API becomes the primary entry point.
        for (const [parameterName, state] of Object.entries(options.optionalConditionParameterStates)) {
            legacyParameters[parameterName] = state === 'absent'
                ? null
                : '__RAWSQL_OPTIONAL_CONDITION_PRESENT__';
        }

        return legacyParameters;
    }
    private applyColumnFilters(query: SelectQuery, options: QueryBuildOptions): SelectQuery {
        const hasIncludeFilters = Array.isArray(options.includeColumns) && options.includeColumns.length > 0;
        const hasExcludeFilters = Array.isArray(options.excludeColumns) && options.excludeColumns.length > 0;

        if (!hasIncludeFilters && !hasExcludeFilters) {
            return query;
        }

        if (hasIncludeFilters && hasExcludeFilters) {
            throw new Error("includeColumns and excludeColumns cannot be used together.");
        }

        const simpleQuery = QueryBuilder.buildSimpleQuery(query);
        const metadata = simpleQuery.selectClause.items.map(item => {
            const name = this.getSelectItemName(item);
            return {
                item,
                normalized: name ? this.normalizeColumnIdentifier(name) : null
            };
        });

        const availableColumns = new Set(
            metadata
                .map(entry => entry.normalized)
                .filter((name): name is string => name !== null)
        );

        const includeFilters = hasIncludeFilters ? this.normalizeColumnList(options.includeColumns!) : null;
        const excludeFilters = hasExcludeFilters ? this.normalizeColumnList(options.excludeColumns!) : null;

        const includeSet = includeFilters ? new Set(includeFilters.map(entry => entry.normalized)) : null;
        const excludeSet = excludeFilters ? new Set(excludeFilters.map(entry => entry.normalized)) : null;

        if (includeFilters) {
            const missing = includeFilters.filter(entry => !availableColumns.has(entry.normalized));
            if (missing.length > 0) {
                throw new Error(
                    `Column${missing.length === 1 ? "" : "s"} not found in SELECT clause: ${missing
                        .map(entry => `'${entry.original}'`)
                        .join(", ")}.`
                );
            }
        }

        if (excludeFilters) {
            const missing = excludeFilters.filter(entry => !availableColumns.has(entry.normalized));
            if (missing.length > 0) {
                throw new Error(
                    `Column${missing.length === 1 ? "" : "s"} not found in SELECT clause: ${missing
                        .map(entry => `'${entry.original}'`)
                        .join(", ")}.`
                );
            }
        }

        const filteredItems = metadata
            .filter(entry => {
                if (!entry.normalized) {
                    return true;
                }
                if (includeSet) {
                    return includeSet.has(entry.normalized);
                }
                if (excludeSet) {
                    return !excludeSet.has(entry.normalized);
                }
                return true;
            })
            .map(entry => entry.item);

        if (filteredItems.length === 0) {
            throw new Error("Column filtering removed every SELECT item.");
        }

        simpleQuery.selectClause.items = filteredItems;
        return simpleQuery;
    }

    private normalizeColumnList(columns: string[]): { normalized: string; original: string }[] {
        return columns.map(column => {
            if (typeof column !== "string") {
                throw new Error("Column filters must be strings.");
            }
            const trimmed = column.trim();
            if (trimmed === "") {
                throw new Error("Column filters must not be empty.");
            }
            return {
                normalized: this.normalizeColumnIdentifier(trimmed),
                original: trimmed
            };
        });
    }

    private normalizeColumnIdentifier(value: string): string {
        return value.trim().toLowerCase();
    }

    private getSelectItemName(item: SelectItem): string | null {
        if (item.identifier) {
            return item.identifier.name;
        }
        if (item.value instanceof ColumnReference) {
            return item.value.column.name;
        }
        return null;
    }

    /**
     * Legacy helper for binding existing named parameters without adding new runtime predicates.
     * Dynamic WHERE-condition injection is no longer supported; use SSSQL scaffold/refresh instead.
     *
     * @param sqlContent Raw SQL string to parse and modify
     * @param filter Named parameters to bind when they already exist in the SQL
     * @returns Modified SelectQuery after binding existing named parameters
     */
    buildFilteredQuery(sqlContent: string, filter: FilterConditions): SelectQuery {
        return this.buildQuery(sqlContent, { filter });
    }

    /**
     * Builds a SelectQuery with only sorting applied.
     * Convenience method for when you only need dynamic ORDER BY clauses.
     *
     * @param sqlContent Raw SQL string to parse and modify
     * @param sort Sort conditions to apply
     * @returns Modified SelectQuery with sort conditions applied
     */
    buildSortedQuery(sqlContent: string, sort: SortConditions): SelectQuery {
        return this.buildQuery(sqlContent, { sort });
    }    /**
     * Builds a SelectQuery with only pagination applied.
     * Convenience method for when you only need LIMIT/OFFSET clauses.
     *
     * @param sqlContent Raw SQL string to parse and modify
     * @param paging Pagination options to apply
     * @returns Modified SelectQuery with pagination applied
     */
    buildPaginatedQuery(sqlContent: string, paging: PaginationOptions): SelectQuery {
        return this.buildQuery(sqlContent, { paging });
    }

    /**
     * Validates SQL content by attempting to parse it.
     * Useful for testing SQL validity without applying any modifications.
     *
     * @param sqlContent Raw SQL string to validate
     * @returns true if SQL is valid, throws error if invalid
     * @throws Error if SQL cannot be parsed
     */
    validateSql(sqlContent: string): boolean {
        try {
            SelectQueryParser.parse(sqlContent);
            return true;
        } catch (error) {
            throw new Error(`Invalid SQL: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
}
